import React from 'react';
import { formatTimestampToDate, getMondayMidnightUTC, getPreviousWeekMonday } from '../services/dailyUsageApiService';
import { ReportingPeriod } from './PeriodToggle';

interface WeekSelectorProps {
  onWeekSelect: (weekDate: string) => void;
  selectedWeek: string;
  reportingPeriod: ReportingPeriod;
}

const WeekSelector: React.FC<WeekSelectorProps> = ({ onWeekSelect, selectedWeek, reportingPeriod }) => {
  const [selectedDate, setSelectedDate] = React.useState<string>('');

  React.useEffect(() => {
    // Set Monday of previous week as default
    const previousWeekMonday = getPreviousWeekMonday();
    setSelectedDate(previousWeekMonday);
    
    // Also notify parent component of the default date
    onWeekSelect(previousWeekMonday);
  }, [onWeekSelect]);

  // Sync with parent's selectedWeek prop when it changes externally (e.g. from period toggle)
  React.useEffect(() => {
    if (selectedWeek && selectedWeek !== selectedDate) {
      setSelectedDate(selectedWeek);
    }
  }, [selectedWeek, selectedDate]);

  // Force re-render when reporting period changes to update labels
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    forceUpdate();
  }, [reportingPeriod]);

  const handleDateChange = (date: string) => {
    if (!date) return;
    
    setSelectedDate(date);
    console.log('📅 [WEEK SELECTOR] Date selected:', date);
    
    // Pass the selected date to parent component
    onWeekSelect(date);
  };

  const getMondayOfWeek = (dateString: string): string => {
    try {
      const mondayTimestamp = getMondayMidnightUTC(dateString);
      return formatTimestampToDate(mondayTimestamp);
    } catch (error) {
      console.error('Error getting Monday of week:', error);
      return dateString;
    }
  };

  const formatWeekRange = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const mondayTimestamp = getMondayMidnightUTC(dateString);
      const monday = new Date(mondayTimestamp);
      const sunday = new Date(mondayTimestamp + 6 * 24 * 60 * 60 * 1000);
      
      return `${monday.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })} - ${sunday.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })}`;
    } catch (error) {
      console.error('Error formatting week range:', error);
      return dateString;
    }
  };

  const format4WeekRange = (startDateString: string): string => {
    if (!startDateString) return '';
    
    try {
      const startMondayTimestamp = getMondayMidnightUTC(startDateString);
      const startMonday = new Date(startMondayTimestamp);
      // 4 weeks = 28 days, so end Sunday is 27 days later (28-1)
      const endSunday = new Date(startMondayTimestamp + 27 * 24 * 60 * 60 * 1000);
      
      return `${startMonday.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })} - ${endSunday.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })}`;
    } catch (error) {
      console.error('Error formatting 4-week range:', error);
      return startDateString;
    }
  };

  // Check if a date interval contains any future weeks
  const containsFutureWeeks = (startDateString: string, periodType: 'weekly' | '4weekly'): boolean => {
    try {
      const startMondayTimestamp = getMondayMidnightUTC(startDateString);
      const endDate = periodType === '4weekly' 
        ? new Date(startMondayTimestamp + 27 * 24 * 60 * 60 * 1000) // 4 weeks - 1 day (end Sunday)
        : new Date(startMondayTimestamp + 6 * 24 * 60 * 60 * 1000);  // 1 week - 1 day (end Sunday)
      
      const today = new Date();
      return endDate > today;
    } catch (error) {
      console.error('Error checking future weeks:', error);
      return false;
    }
  };

  // Generate list of Mondays going back 12 weeks
  const generateMondayOptions = (): Array<{value: string, label: string}> => {
    const options: Array<{value: string, label: string}> = [];
    const today = new Date();
    
    // Start from current Monday and go back 12 weeks
    for (let i = 0; i < 12; i++) {
      const date = new Date(today);
      const currentDayOfWeek = today.getDay();
      // Calculate days to subtract to get to Monday of this week, then add i*7 more days
      const daysToMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
      date.setDate(today.getDate() - daysToMonday - (i * 7));
      
      const mondayString = date.toISOString().split('T')[0];
      
      // Skip this option if it contains future weeks
      if (containsFutureWeeks(mondayString, reportingPeriod)) {
        continue;
      }
      
      // Use different labels based on reporting period
      let label: string;
      if (reportingPeriod === '4weekly') {
        const fourWeekRange = format4WeekRange(mondayString);
        label = `${date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })} (${fourWeekRange} - 4 weeks)`;
      } else {
        const weekRange = formatWeekRange(mondayString);
        label = `${date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })} (${weekRange})`;
      }
      
      options.push({
        value: mondayString,
        label
      });
    }
    
    return options;
  };

  const mondayOptions = generateMondayOptions();

  return (
    <div className="week-selector-compact">
      <label htmlFor="week-select" className="week-label">
        Week starting:
      </label>
      <select
        id="week-select"
        value={selectedDate}
        onChange={(e) => handleDateChange(e.target.value)}
        className="week-select-dropdown"
      >
        {mondayOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default WeekSelector; 